<template>
  <div>

    <Field :name="id" :rules="rules" v-slot="{ errors, setErrors }">

      <div class="mb-2">

        <label :id="fileType" v-if="label" class="label">
          {{ label }}
          <RequiredAsterisk v-if="rules.includes('required')" />
        </label>

        <div class="compound-data">

          <label :id="getElementId('file', fileType)" :for="id" class="button is-link is-outlined is-flex-grow-1">
            <span class="icon"><i class="fas fa-upload"></i></span>
            <span>{{ fileButtonLabel }}</span>

            <input
              type="file"
              :accept="`.${fileType}`"
              class="is-hidden"
              :ref="id"
              :id="id"
              @change="onFileChange($event, setErrors)"
            />
          </label>

          <a
            :id="getElementId('download', fileType)"
            :href="fileUrl"
            download
            :style="getDownloadStyles()"
            class="button is-ghost is-flex-grow-1"
          >
            Download {{ fileTypeUpperCase }}
          </a>

          <span
            :id="getElementId('delete', fileType)"
            :title="getBinTitle()"
            :class="getBinClasses()"
            style="height: 2.3em; filter: invert(12%) sepia(96%) saturate(4579%) hue-rotate(344deg) brightness(89%) contrast(93%);"
            @click="sendConfirm(fileType)"
          >
            <i class="fas fa-trash-alt"></i>
          </span>

        </div>

        <!-- Validation message -->
        <p v-if="errors.length" class="help is-danger" :id="`${id}Err`">
          {{ errors[0] }}
        </p>

      </div>

    </Field>

    <span v-if="fileName" class="tag pill">
      {{ fileName }}
      <button
        type="button"
        class="delete is-small"
        @click="resetFileInput"
        :aria-label="`Remove ${fileName}`"
      ></button>
    </span>

  </div>
</template>

<script>
import { MarinLitDataService } from "@/api";
import RequiredAsterisk from "@/components/forms/required-asterisk.vue";
import { Field } from "vee-validate";
import { eventBus } from "@/eventBus";
import { useAuthStore } from "@/store/modules/auth";
import { useMarinLitSearchStore } from "@/store/modules/marinlit/search";
import { useMerckIndexSearchStore } from "@/store/modules/merckindex/search";

export default {
  name: "FileInput",
  setup() {
    const marinLitSearchStore = useMarinLitSearchStore();
    const merckIndexSearchStore = useMerckIndexSearchStore();

    return {
      marinLitSearchStore,
      merckIndexSearchStore
    };
  },
  data() {
    return {
      file: null,
      fileRoot: {
        MarinLitEdit: import.meta.env.VITE_MARINLIT_FILES,
        MerckIndexEdit: import.meta.env.VITE_MERCKINDEX_FILES,
      }
    };
  },
  props: {
    id: {
      type: String,
      required: true,
      validator: value => value.length
    },
    label: {
      type: String,
      default: "",
    },
    fileType: {
      type: String,
      default: "",
      validator: (value) => ["png", "mol", "cdx", "xml"].includes(value),
    },
    filePath: {
      type: String,
      default: ""
    },
    fileSizeLimit: {
      type: Number,
      default: 128 * 1024
    },
    rules: {
      type: String,
      default: ""
    },
    _value: {
      type: Boolean,
      default: false
    },
    isActive: {
      type: Boolean,
      default: false
    },
    isReadOnly: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    StatusMerckIndexEdit() {
      return this.merckIndexSearchStore.SearchParams.status.value;
    },
    StatusMarinLitEdit() {
      return this.marinLitSearchStore.SearchParams.status.value;
    },
    fileButtonLabel() {
      return `${this.file || this.filePath ? "Replace" : "Upload"} ${this.fileTypeUpperCase} file`;
    },

    fileName() {
      return this.file && this.file.name ? this.file.name : "";
    },
    fileTypeUpperCase() {
      return this.fileType.toUpperCase();
    },
    fileUrl() {
      return `${this.fileRoot[this.$route.name]}/${this.filePath}`;
    },
    fileSizeError() {
      return `File must not be larger than ${this.fileSizeLimit / 1024}KB`;
    },
    fileTypeError() {
      return `File type must be ${this.fileTypeUpperCase}`;
    },
  },
  methods: {
    async onFileChange(e, setErrors) {
      // Validate rules, eg. required
      const files = e.target.files || e.dataTransfer.files;

      if (!files.length) {
        return;
      }

      this.file = files[0];
      let newErrors = [];

      // Validate file type
      if (!this.file.name.toLowerCase().endsWith(this.fileType)) {
        newErrors.push(this.fileTypeError);
        this.resetFileInput();
      }

      // Validate file size
      if (this.file.size > this.fileSizeLimit) {
        newErrors.push(this.fileSizeError);
        this.resetFileInput();
      }

      if (newErrors.length) {
        setErrors(newErrors);
        return;
      }

      // For MarinLit ONLY, try to extract molecular formula, exact mass, standard
      // InChI and InChIKey from a given MOL file
      if (this.$route.name === "MarinLitEdit" && this.fileType === "mol") {
        this.file = null; // Hide file capsule until validated
        await this.extractMolData(files[0], setErrors);
        return;
      }

      // Success
      setErrors([]);
      this.$emit("update", this.fileType, this.file);
    },

    async extractMolData(file, setErrors) {

      let formData = new FormData();
      formData.append("file", file);
      // Only attempt this if you know that the user has permission to work on MarinLit
      // otherwise the API will return 403 Forbidden and the file won't be accepted.
      const result = await MarinLitDataService.getCompoundDataFromMolFile(formData);
      if (result.Data) {
        this.file = file;
        this.$emit("update", this.fileType, this.file, result.Data);
      } else if (result.ServiceStatus?.Error?.status === 401) { // avoids 401 error due to token expiration
        console.log('token expiration');
        // renews token and makes the call again
        const authStore = useAuthStore();
        await authStore.updateAuth();
        const secondResult = await MarinLitDataService.getCompoundDataFromMolFile(formData);
        if (secondResult.Data) {
          this.file = file;
          this.$emit("update", this.fileType, this.file, secondResult.Data);
        } else {
          console.log('second attempt failure');
          this.handleError(setErrors);
        }
      } else {
        this.handleError(setErrors);
      }
    },
    handleError(setErrors) {
      setErrors(["Failed to extract compound data from MOL file"]);
      this.resetFileInput();
    },

    resetFileInput() {
      this.file = null;
      this.$refs[this.id].value = null;
      this.$emit("update", this.fileType, null);
    },

    sendConfirm(type) {
      eventBus.emit("confirm", type);
    },
    getElementId(action, type) {
      return `${action}-${type}`;
    },
    getBinTitle() {
      return (this.filePath && !this.isActive && this.fileType !== "xml")
        ? `delete ${this.fileType} file`
        : "";
    },
    getBinClasses() {
      return (
        this[`Status${this.$route.name}`] === "draft" &&
        this.filePath &&
        !this.isActive &&
        this.fileType !== "xml" &&
        !this.fileName &&
        !this.isReadOnly
      )
        ? "icon is-flex-grow-1 is-clickable"
        : "is-invisible is-flex-grow-1";
    },

    getDownloadStyles() {
      return (this.isActive || this.filePath)
        ? "cursor:pointer; opacity:1; border:0; visibility: visible"
        : "visibility:hidden;"
    }
  },

  components: {
    RequiredAsterisk,
    Field
  }
};
</script>